import { Box, Button, TextField, Typography } from "@mui/material";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  createStudyAbroadPurchaseAsync,
  verifyPurchaseStudyAbroadAsync,
} from "src/redux/services/studyAbroadServices";
import { useSnackbar } from "src/Component/Snakbar";

const StudyAbroadPurchase = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Load Razorpay script dynamically
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePayment = async (order) => {
    if (!window.Razorpay) {
      console.error("Razorpay not loaded");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount * 100,
      currency: order.currency || "INR",
      name: "Study Abroad Purchase",
      description: "Purchase Study Abroad Package",
      order_id: order.razorpayOrderId,
      handler: function (response) {
        const verifyPayload = {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        };

        dispatch(verifyPurchaseStudyAbroadAsync(verifyPayload)).then((res) => {
          if (res?.payload?.success) {
            showSnackbar(
              "Payment successful! Your purchase has been completed.",
              "success",
            );
            navigate("/dashboard/home");
          } else {
            showSnackbar(
              "Payment verification failed. Please try again.",
              "error",
            );
          }
        });
      },
      theme: {
        color: "#00999E",
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", function (response) {
      alert("Payment failed. Please try again.");
    });

    rzp.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || amount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }
    if (!razorpayLoaded) {
      setError("Payment gateway is loading, please try again in a moment.");
      return;
    }
    setIsSubmitting(true);

    // 1. Create order API call
    const payload = {
      amount: parseFloat(amount),
      currency: "INR",
      paymentMethod: "online",
      description: "Study Abroad Full Package",
    };
    try {
      const orderResponse = await dispatch(
        createStudyAbroadPurchaseAsync(payload),
      ).unwrap();

      // orderResponse should contain { razorpay_order_id, amount, currency, ... }
      if (
        orderResponse &&
        orderResponse.data &&
        orderResponse.data.razorpayOrderId
      ) {
        await handlePayment(orderResponse.data);
        setError("");
      } else {
        setError("Failed to cre13ate order. Please try again.");
      }
    } catch (err) {
      setError("Failed to create order. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <Box
      sx={{
        pt: 0,
        height: "92vh",
        width: "100%",
        position: "relative",
        backgroundColor: "#f5f5f5",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: { xs: 2, md: 4 },
      }}
    >
      {/* Left Corner Back Button */}
      <Button
        onClick={handleBack}
        variant="contained"
        startIcon={<NavigateBeforeRoundedIcon fontSize="small" />}
        sx={{
          position: "absolute",
          top: { xs: 40, md: 40 },
          left: { xs: 16, md: 20 },
          backgroundColor: "#00999E",
          color: "#ffffff",
          "&:hover": {
            color: "#00999E",
            backgroundColor: "#f0f0f0",
          },
          fontWeight: 600,
          borderRadius: "5px",
          minWidth: { xs: "80px", md: "100px" },
          py: { xs: 1, md: 1.2 },
          fontSize: { xs: "0.8rem", md: "1rem" },
        }}
      >
        Back
      </Button>

      {/* Center Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          borderRadius: 2,
          p: { xs: 3, md: 5 },
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: { xs: "90%", sm: "80%", md: "50%", lg: "40%" },
          backgroundColor: "white",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2rem" },
          }}
        >
          Purchase Study Abroad
        </Typography>

        <TextField
          id="amount"
          label="Amount (INR)"
          variant="outlined"
          type="number"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={!!error}
          helperText={error}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={!razorpayLoaded || isSubmitting}
          sx={{
            backgroundColor: "#00999E",
            "&:hover": {
              backgroundColor: "#00777b",
            },
            py: { xs: 1.2, md: 1.5 },
            fontWeight: 600,
            fontSize: { xs: "0.95rem", md: "1rem" },
          }}
          fullWidth
        >
          {razorpayLoaded
            ? isSubmitting
              ? "Processing..."
              : "Purchase Now"
            : "Loading Payment..."}
        </Button>
      </Box>
    </Box>
  );
};

export default StudyAbroadPurchase;
